import React from 'react';
import { Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import colors from '../colors';
import spacing from '../spacing';
import typography from '../typography';

/**
 * Badge Component
 * @component
 * @description Modern badges for status indicators, labels, and categories
 * 
 * @example
 * // Simple badge
 * <Badge color="primary">Nuevo</Badge>
 * 
 * @example
 * // Football-specific badges
 * <Badge type="win">Victoria</Badge>
 * <Badge type="yellow-card">Tarjeta</Badge>
 * 
 * @param {string} variant - 'filled' | 'outlined'
 * @param {string} color - 'primary' | 'secondary' | 'success' | 'error'
 * @param {string} size - 'small' | 'medium' | 'large'
 * @param {string} type - 'win' | 'draw' | 'loss' | 'yellow-card' | 'red-card'
 */

const StyledBadge = styled(Chip)(({ variant, color, size, theme }) => ({
  borderRadius: spacing.borderRadius.full,
  fontWeight: typography.fontWeight.semibold,
  transition: 'all 0.2s ease',
  
  // Size variants
  ...(size === 'small' && {
    height: spacing[5],
    fontSize: typography.fontSize.xs,
    '& .MuiChip-label': {
      padding: `0 ${spacing[2]}px`,
    },
  }),
  
  ...(size === 'medium' && {
    height: spacing[6],
    fontSize: typography.fontSize.sm,
    '& .MuiChip-label': {
      padding: `0 ${spacing[3]}px`,
    },
  }),
  
  ...(size === 'large' && {
    height: spacing[8],
    fontSize: typography.fontSize.base,
    '& .MuiChip-label': {
      padding: `0 ${spacing[4]}px`,
    },
  }),

  // Filled variant
  ...(variant === 'filled' && {
    '&.MuiChip-colorPrimary': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    '&.MuiChip-colorSecondary': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  }),

  // Outlined variant
  ...(variant === 'outlined' && {
    borderWidth: spacing.borderWidth[2],
    '&.MuiChip-colorPrimary': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),
}));

// Badge preconfigurados para fútbol
const WinBadge = styled(StyledBadge)(({ theme }) => ({
  backgroundColor: colors.football.win,
  color: theme.palette.getContrastText(colors.football.win),
  '&:hover': {
    backgroundColor: colors.primary[600],
  },
}));

const DrawBadge = styled(StyledBadge)(({ theme }) => ({
  backgroundColor: colors.football.draw,
  color: theme.palette.getContrastText(colors.football.draw),
  '&:hover': {
    backgroundColor: colors.warning.main,
  },
}));

const LossBadge = styled(StyledBadge)(({ theme }) => ({
  backgroundColor: colors.football.loss,
  color: theme.palette.getContrastText(colors.football.loss),
  '&:hover': {
    backgroundColor: colors.error.main,
  },
}));

const YellowCardBadge = styled(StyledBadge)(({ theme }) => ({
  backgroundColor: colors.football.yellowCard,
  color: theme.palette.getContrastText(colors.football.yellowCard),
  border: `2px solid ${theme.palette.text.primary}`,
  fontWeight: typography.fontWeight.bold,
}));

const RedCardBadge = styled(StyledBadge)(({ theme }) => ({
  backgroundColor: colors.football.redCard,
  color: theme.palette.getContrastText(colors.football.redCard),
  border: `2px solid ${theme.palette.text.primary}`,
  fontWeight: typography.fontWeight.bold,
}));

const Badge = ({
  children,
  variant = 'filled',
  color = 'primary',
  size = 'medium',
  type, // 'win', 'draw', 'loss', 'yellow-card', 'red-card'
  onDelete,
  icon,
  ...props
}) => {
  // Usar badges preconfigurados para tipos de fútbol
  if (type === 'win') {
    return <WinBadge label={children} size={size} icon={icon} onDelete={onDelete} {...props} />;
  }
  
  if (type === 'draw') {
    return <DrawBadge label={children} size={size} icon={icon} onDelete={onDelete} {...props} />;
  }
  
  if (type === 'loss') {
    return <LossBadge label={children} size={size} icon={icon} onDelete={onDelete} {...props} />;
  }
  
  if (type === 'yellow-card') {
    return <YellowCardBadge label={children} size={size} icon={icon} onDelete={onDelete} {...props} />;
  }
  
  if (type === 'red-card') {
    return <RedCardBadge label={children} size={size} icon={icon} onDelete={onDelete} {...props} />;
  }
  
  // Badge genérico
  return (
    <StyledBadge
      label={children}
      variant={variant}
      color={color}
      size={size}
      icon={icon}
      onDelete={onDelete}
      {...props}
    />
  );
};

export default Badge;
